import { useState, useEffect } from 'react'
import { locationAPI } from '../services/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('useEntitySearch')

/**
 * Custom hook for entity search with debouncing
 * @param {Object} params - Search parameters
 * @param {string} params.query - Search query string
 * @param {Object} params.userLocation - User location object with latitude/longitude
 * @param {number} params.radius - Search radius in km
 * @param {string} params.searchLocation - Location search string
 * @param {number} params.debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns {Object} - { results, isSearching, showDropdown, setShowDropdown }
 */
export function useEntitySearch({ 
  query, 
  userLocation, 
  radius, 
  searchLocation,
  sortBy = 'recent',
  debounceMs = 300 
}) {
  const [results, setResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  useEffect(() => {
    if ((query ?? '').length > 1) {
      const timeoutId = setTimeout(async () => {
        setIsSearching(true)
        try {
          let params = {
            query: query.toLowerCase(),
            limit: 10
          }
          
          // Only add location if we have a search location set
          // This ensures name-only searches work globally when location is cleared
          if (userLocation && searchLocation.trim()) {
            params.latitude = userLocation.latitude
            params.longitude = userLocation.longitude
            params.radius = radius * 1000 // Convert to meters
          }
          
          // Add sort if not default
          if (sortBy && sortBy !== 'recent') {
            params.sort = sortBy
          }
          
          const response = await locationAPI.searchEntities(params)
          setResults(response.data.entities || [])
          setShowDropdown(true)
        } catch (error) {
          logger.error('Entity search failed', error)
          setResults([])
        } finally {
          setIsSearching(false)
        }
      }, debounceMs)
      
      return () => clearTimeout(timeoutId)
    } else {
      setResults([])
      setShowDropdown(false)
      return undefined
    }
  }, [query, userLocation, radius, searchLocation, sortBy, debounceMs])

  return {
    results,
    isSearching,
    showDropdown,
    setShowDropdown
  }
}
